type ActivityConnection = {
  id: string;
  created_at?: string | null;
  last_message_at?: string | null;
};

type ActivityDirectMessage = {
  id: string;
  connection_id?: string | null;
  content?: string | null;
  created_at?: string | null;
  read_at?: string | null;
  sender_id?: string | null;
};

export type DirectMessageConversationSummary<TMessage extends ActivityDirectMessage> = {
  lastMessage: TMessage | null;
  unreadCount: number;
  unread: boolean;
};

const toTimestamp = (value?: string | null) => {
  if (!value) {
    return 0;
  }

  const parsed = new Date(value).getTime();
  return Number.isFinite(parsed) ? parsed : 0;
};

export const summarizeDirectMessageConversations = <
  TConnection extends ActivityConnection,
  TMessage extends ActivityDirectMessage,
>(
  connections: TConnection[],
  messages: TMessage[],
  currentUserId: string,
) => {
  const messagesByConnection = new Map<string, TMessage[]>();

  messages.forEach((message) => {
    if (!message.connection_id) {
      return;
    }

    const existing = messagesByConnection.get(message.connection_id) ?? [];
    existing.push(message);
    messagesByConnection.set(message.connection_id, existing);
  });

  return [...connections]
    .map((connection) => {
      const conversationMessages = (messagesByConnection.get(connection.id) ?? []).sort(
        (left, right) => toTimestamp(right.created_at) - toTimestamp(left.created_at),
      );
      const lastMessage = conversationMessages[0] ?? null;
      const unreadCount = conversationMessages.filter(
        (message) => message.sender_id !== currentUserId && !message.read_at,
      ).length;

      return {
        connection,
        lastMessage,
        unreadCount,
        unread: unreadCount > 0,
      } satisfies { connection: TConnection } & DirectMessageConversationSummary<TMessage>;
    })
    .sort((left, right) => {
      const leftTime = toTimestamp(
        left.lastMessage?.created_at ?? left.connection.last_message_at ?? left.connection.created_at,
      );
      const rightTime = toTimestamp(
        right.lastMessage?.created_at ?? right.connection.last_message_at ?? right.connection.created_at,
      );
      return rightTime - leftTime;
    });
};
